import * as React from 'react';
import { AScene } from '@eagerlogic/react-mvc';
import { TeamModel, UserItemModel } from './model/TeamModel';
import { TeamController } from './controller/TeamController';
import { ISceneFactory } from 'scenes/ISceneFactory';
import { HistoryItem } from 'utils/Navigation';
import { Paper } from 'components/paper';
import { Button, Table, TableHead, TableRow, TableCell, TableBody, Icon } from '@material-ui/core';
import { MessageDialog } from 'components/MessageDialog';
import { CreateTeamDialog } from './CreateTeamDialog';
import { InviteUserDialog } from './InviteUserDialog';
import { EditUserDialog } from './EditUserDialog';
import { ChangeVacationCountDialog } from './ChangeVacationCountDialog';

export class TeamScene extends AScene<Props, TeamModel, TeamController> {

    public constructor(props: Props) {
        super(props);

        this.handleCreateTeamClicked = this.handleCreateTeamClicked.bind(this);
        this.handleInviteUserClicked = this.handleInviteUserClicked.bind(this);
    }

    protected createControlller(): TeamController {
        return new TeamController();
    }

    private handleCreateTeamClicked() {
        this.controller.onCreateTeamClicked();
    }

    private handleInviteUserClicked() {
        this.controller.onInviteUserClicked();
    }

    public render(): React.ReactNode {
        if (this.model.isInit) {
            return (<></>);
        }
        return (
            <div style={{ width: '100%', padding: '80px' }}>
                {this.renderTeamsPaper()}
                {this.renderUsersPaper()}

                {this.model.createTeamDialogModel != null && (
                    <CreateTeamDialog model={this.model.createTeamDialogModel} controller={this.controller} />
                )}
                {this.model.inviteUserDialogModel != null && (
                    <InviteUserDialog model={this.model.inviteUserDialogModel} teams={this.model.teams} controller={this.controller} />
                )}
                {this.model.editUserDialogModel != null && (
                    <EditUserDialog model={this.model.editUserDialogModel} teams={this.model.teams} controller={this.controller} />
                )}
                {this.model.changeVacationCountDialogModel != null && (
                    <ChangeVacationCountDialog model={this.model.changeVacationCountDialogModel} controller={this.controller} />
                )}

                {this.model.deletingTeamId != null && (
                    <MessageDialog
                        message={"Are you sure you want to delete this team?"}
                        okButtonTitle="Yes"
                        cancelButtonTitle="No"
                        onOkButtonClicked={() => { this.controller.onSureDeleteTeam() }}
                        onCancelButtonClicked={() => { this.controller.onCancelDeleteTeam() }}
                    />
                )}
                {this.model.deletingUser != null && (
                    <MessageDialog
                        message={this.model.deletingUser.pendingInvitation ? "Are you sure you want to revoke this invitation?" : "Are you sure you want to delete this user?"}
                        okButtonTitle="Yes"
                        cancelButtonTitle="No"
                        onOkButtonClicked={() => { this.controller.onSureDeleteUser() }}
                        onCancelButtonClicked={() => { this.controller.onCancelDeleteUser() }}
                    />
                )}
            </div>
        );
    }

    private renderTeamsPaper() {
        return (
            <Paper title="Teams" desc="You can use teams to organize your co-workers.">
                <Table style={{ width: '100%' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell style={{ width: '200px' }}>User count</TableCell>
                            <TableCell align="right" style={{ width: '100px' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {this.model.teams.map((team, index) => {
                            return (
                                <TableRow key={"teamrow-" + index}>
                                    <TableCell>{team.name}</TableCell>
                                    <TableCell>{team.userCount}</TableCell>
                                    <TableCell align="right">
                                        {team.userCount < 1 && (
                                            <Icon style={{ color: '#c32', margin: '2px', cursor: 'pointer', fontSize: '16px' }} onClick={() => { this.controller.onDeleteTeamClicked(team.id) }}>delete</Icon>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>

                <div style={{ width: '100%', marginTop: '32px' }}>
                    <Button color="default" variant="contained" onClick={this.handleCreateTeamClicked}>Create team</Button>
                </div>
            </Paper>
        );
    }

    private renderUsersPaper() {
        return (
            <Paper title="Users" desc="Here you can manage your co-workers. You can invite, delete, or edit users.">
                <Table style={{ width: '100%' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Team</TableCell>
                            <TableCell>Pending</TableCell>
                            <TableCell align="right" style={{ width: '100px' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {this.model.users.map((user, index) => {
                            return (
                                <TableRow key={"userrow-" + index}>
                                    <TableCell>{user.name}</TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>{user.roleTitle}</TableCell>
                                    <TableCell>{user.teamName != null ? user.teamName : "No team"}</TableCell>
                                    <TableCell>{user.pendingInvitation ? "Pending" : ""}</TableCell>
                                    <TableCell align="right">
                                        {this.renderUserActions(user)}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>

                <div style={{ width: '100%', marginTop: '32px' }}>
                    <Button color="default" variant="contained" onClick={this.handleInviteUserClicked}>Invite user</Button>
                </div>
            </Paper>
        );
    }

    private renderUserActions(user: UserItemModel) {
        if (user.pendingInvitation) {
            return (
                <>
                    <Icon style={{ color: '#28a', margin: '2px', cursor: 'pointer', fontSize: '16px' }} onClick={() => { this.controller.onResendInvitationClicked(user.id) }}>mail</Icon>
                    <Icon style={{ color: '#c32', margin: '2px', cursor: 'pointer', fontSize: '16px' }} onClick={() => { this.controller.onRevokeInvitationClicked(user) }}>delete</Icon>
                </>
            );
        }
        return (
            <>
                <Icon style={{ color: '#ca2', margin: '2px', cursor: 'pointer', fontSize: '16px' }} onClick={() => { this.controller.onEditUserClicked(user) }}>edit</Icon>
                <Icon style={{ color: '#888', margin: '2px', cursor: 'pointer', fontSize: '16px' }} onClick={() => { this.controller.onChangeVacationCountClicked(user) }}>keyboard</Icon>
                <Icon style={{ color: '#c32', margin: '2px', cursor: 'pointer', fontSize: '16px' }} onClick={() => { this.controller.onDeleteUserClicked(user) }}>delete</Icon>
            </>
        );
    }

}

export class Props {

}

export class TeamSceneFactory implements ISceneFactory {

    create(historyItem: HistoryItem): React.ReactNode {
        return (
            <TeamScene />
        );
    }

}
